/**
 * A real GoTrue session (SEC-03).
 *
 * The console used to authenticate with `VITE_SUPABASE_TOKEN` — a JWT baked into
 * the bundle at build time. Every browser that loaded the app was the same user,
 * with an expiry measured in years, and there was no sign-in, no sign-out, and
 * no way to revoke one person without rebuilding and redeploying for everyone.
 * `auth.uid()` was a build constant, so every RLS policy and every audit row
 * recorded that one identity no matter who was actually at the keyboard. The
 * access work in core 0104-0116 is only meaningful if the platform knows WHO is
 * asking; this is the part that makes it know.
 *
 * Deliberately framework-free — no React, no Supabase client library. The
 * console already speaks to PostgREST through @solum/sdk; adding a second
 * transport with its own opinions about storage and refresh would give us two
 * sources of truth about who is signed in.
 *
 * STORAGE. Tokens live in localStorage. That is a real tradeoff and worth
 * naming: script running in the page can read them. The alternative — an
 * httpOnly cookie — cannot be set by a SPA talking directly to GoTrue's token
 * endpoint; it needs a same-origin backend to broker the exchange. The console
 * has no such backend today. The mitigations that DO apply are applied: the
 * access token is short-lived (an hour), refresh tokens rotate on every use, and
 * a detected reuse invalidates the family server-side.
 *
 * ── SEC-08 (#371): this was REVIEWED, and it is a decision, not an oversight ──
 *
 * Reopened by the 2026-07-31 platform review and closed deliberately. What the
 * alternative actually costs, measured against the tree rather than in the
 * abstract:
 *
 *   · PostgREST authenticates by `Authorization: Bearer` ONLY — it never reads
 *     cookies. So a cookie session is not a change to this file; it is a new
 *     always-in-path BFF that swaps cookie→bearer for every single data call.
 *   · That BFF has to be SAME-ORIGIN with the app, and neither the console nor
 *     the portal is in dist/docker-compose.yml at all. `dist/.env.example`
 *     documents the console as a DIFFERENT origin (SOLUM_CORS_ORIGINS). So the
 *     work is a hosting story the product does not have yet, not a proxy.
 *   · The extension panel runs on `chrome-extension://…` and cannot usefully
 *     hold a first-party cookie for the API host, so it keeps bearer either
 *     way. Cookies therefore do not REPLACE this code path, they ADD a second
 *     one — the exact duplication packages/auth was extracted to prevent
 *     (see index.ts).
 *   · Cookies would also introduce CSRF, a class the bearer design does not
 *     have, on every mutating call.
 *
 * What the exposure is, stated honestly: a stolen token is SESSION THEFT, not
 * privilege escalation. RLS still applies, `assert_can_act` still refuses, and
 * Kong rate-limits /rest/v1 per Authorization header. The two known same-origin
 * script sinks were removed in #374/#401 (DocumentFrame's empty `sandbox`, and
 * brand.logo off dangerouslySetInnerHTML), both proven by execution tests with
 * a published mutation table — not by asserting an attribute is present.
 *
 * So the residual risk was never "can script read this", it was "for how long
 * is that worth anything". Nothing bounded it: GoTrue refresh tokens do not
 * expire on their own, so one exfiltrated token was an INDEFINITE session — and
 * rotation can make the theft quiet: GoTrue accepts the revoked parent of the
 * active token with no time bound, so a thief who refreshes in step is not
 * detected. An older-token replay may revoke the family and sign the user out,
 * which reads as a glitch (#2516).
 *
 * That is what the fix addressed: GOTRUE_SESSIONS_INACTIVITY_TIMEOUT and
 * GOTRUE_SESSIONS_TIMEBOX in dist/docker-compose.yml now cap a session in time,
 * which is the dimension rotation, RLS and rate limiting all leave open. The
 * ratchet is packages/installer/test/distribution.test.ts — removing either
 * setting turns it red.
 *
 * Two things this does NOT cover, so nobody re-derives the picture from here:
 *
 *   1. A CSP is still not shipped anywhere in the repo (#456). It is the other
 *      half of what makes localStorage survivable, and it is tracked.
 *   2. `documents.render_*` still does not HTML-escape values (deliberately out
 *      of scope in #401 — `0008` records that a global escape was tried and
 *      reverted, so it needs to be per-value with an opt-in). `sandbox=""` is
 *      what stands between that markup and this origin. Do not weaken it.
 *
 * If the deployment shape changes — a multi-tenant hosted console, or a real
 * same-origin backend arriving for other reasons — this decision is worth
 * retaking, because its main premise (single-tenant, self-hosted, operator's
 * own edge) will have gone.
 */

import {
  readFactorsFromUser,
  readTotpEnrollment,
  type MfaFactor,
  type TotpEnrollment,
} from './mfa.js'
import {
  normalizeRecoveryCode,
  readRecoveryCodeReveal,
  readRecoveryCodeStatus,
  type RecoveryCodeReveal,
  type RecoveryCodeStatus,
} from './recovery-codes.js'
import { assuranceOf } from './assurance.js'
import type { AccountRoster } from './accounts.js'
import type { RecoveryGrant } from './recovery.js'
import { isSignInChallenge, type SignInChallenge } from './signin-challenge.js'
import {
  oauthAuthorizationId, oauthClientId, readOAuthAuthorization, readOAuthDecision, readOAuthGrants,
  type OAuthAuthorization, type OAuthGrant,
} from './oauth.js'

export interface Session {
  accessToken: string
  refreshToken: string
  /** epoch ms — computed from expires_in at receipt, so it survives a clock that is merely offset */
  expiresAt: number
  user: { id: string; email: string | null }
}

/** The two legitimate GoTrue signup outcomes. */
export interface SignUpResult {
  /** Null when email confirmation is required; present on autoconfirm stacks. */
  session: Session | null
  confirmationRequired: boolean
}

export class AuthError extends Error {
  constructor(message: string, readonly status?: number, readonly code?: string) {
    super(message)
    this.name = 'AuthError'
  }
}

type GoTrueError = { error_code?: unknown; code?: unknown }

async function gotrueErrorCode(res: Response): Promise<string | null> {
  try {
    const body = await res.json() as GoTrueError
    const code = typeof body.error_code === 'string' ? body.error_code
      : typeof body.code === 'string' ? body.code : null
    return code && /^[a-z0-9_]{1,80}$/.test(code) ? code : null
  } catch { return null }
}

const recoveryFailure = async (
  res: Response,
  fallback: string,
  operation: 'status' | 'generate' | 'verify' | 'regenerate' | 'delete',
): Promise<AuthError> => {
  const code = await gotrueErrorCode(res)
  if (operation === 'status' && res.status === 404 && code === 'mfa_factor_not_found') {
    return new AuthError('Recovery codes are not enrolled.', res.status, code)
  }
  const message: Record<string, string> = {
    mfa_recovery_codes_enroll_not_enabled: 'Recovery codes are not available on this deployment.',
    mfa_recovery_codes_verify_not_enabled: 'Recovery code verification is not available on this deployment.',
    mfa_recovery_codes_locked: 'Too many recovery attempts. Try again later.',
    insufficient_aal: 'Verify your sign-in before changing recovery codes.',
    mfa_recovery_codes_sole_factor: 'Add and verify an authenticator app before creating recovery codes.',
    mfa_verified_factor_exists: 'Recovery codes already exist. Regenerate them instead.',
    mfa_factor_not_found: operation === 'verify'
      ? 'That recovery code was not accepted.' : 'Recovery codes are not enrolled.',
    mfa_verification_failed: 'That recovery code was not accepted.',
  }
  return new AuthError(message[code ?? ''] ?? fallback, res.status, code ?? undefined)
}

const STORAGE_KEY = 'solum.session'

/** Refresh this far BEFORE expiry, so a request never carries a token that dies in flight. */
const REFRESH_SKEW_MS = 60_000

interface TokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  user?: { id: string; email?: string | null }
}

interface ChallengeResponse {
  id: string
  expires_at?: number
}

const FACTOR_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function factorId(value: string): string {
  if (!FACTOR_ID.test(value)) throw new AuthError('That authentication factor is invalid.')
  return value
}

function totpCode(value: string): string {
  const code = value.trim()
  if (!/^\d{6}$/.test(code)) throw new AuthError('Enter the six-digit code from your authenticator app.')
  return code
}

function decodeSub(jwt: string): { id: string; email: string | null } {
  // The user object is authoritative when GoTrue sends one; this is the fallback
  // for the refresh response, which does not always include it. Reading the
  // payload is NOT verification — the server verifies, we only need the id to
  // label the UI.
  try {
    const [, payload] = jwt.split('.')
    const claims = JSON.parse(atob(payload!.replace(/-/g, '+').replace(/_/g, '/'))) as
      { sub?: string; email?: string }
    return { id: claims.sub ?? '', email: claims.email ?? null }
  } catch {
    return { id: '', email: null }
  }
}

function toSession(r: TokenResponse, now = Date.now()): Session {
  return {
    accessToken: r.access_token,
    refreshToken: r.refresh_token,
    expiresAt: now + r.expires_in * 1000,
    user: r.user?.id ? { id: r.user.id, email: r.user.email ?? null } : decodeSub(r.access_token),
  }
}

type MaybePromise<T> = T | Promise<T>

/**
 * Session persistence is injected because an MV3 worker has no localStorage.
 * The adapter owns one wire format; panel and worker must never serialize a
 * second shape beside it. `withLock` is optional for ordinary pages and is the
 * cross-context refresh lock for extension origins.
 */
export interface SessionStorageAdapter {
  load(): MaybePromise<Session | null>
  save(session: Session): MaybePromise<void>
  clear(): MaybePromise<void>
  subscribe?(listener: (session: Session | null) => void): () => void
  withLock?<T>(name: string, task: () => Promise<T>): Promise<T>
}

/** Refresh is not activity. Hosts explicitly record real interaction. */
export interface SessionActivityPolicy {
  record(at: number): MaybePromise<void>
  isExpired(at: number): MaybePromise<boolean>
  clear(): MaybePromise<void>
}

const validSession = (value: unknown): Session | null => {
  const s = value as Partial<Session> | null
  return s?.accessToken && s.refreshToken && typeof s.expiresAt === 'number' && s.user?.id
    ? s as Session
    : null
}

/** Existing console/portal behaviour, expressed through the same adapter seam. */
export function localSessionStorage(key = STORAGE_KEY): SessionStorageAdapter {
  return {
    load() {
      try {
        const raw = globalThis.localStorage?.getItem(key)
        return raw ? validSession(JSON.parse(raw)) : null
      } catch { return null }
    },
    save(session) {
      try { globalThis.localStorage?.setItem(key, JSON.stringify(session)) } catch { /* private mode */ }
    },
    clear() {
      try { globalThis.localStorage?.removeItem(key) } catch { /* private mode */ }
    },
  }
}

export interface AuthClientOptions {
  /** GoTrue base, e.g. https://host/auth/v1 */
  url: string
  /** the anon apikey GoTrue expects alongside the request */
  anonKey?: string
  /** Defaults to the existing localStorage contract. */
  storage?: SessionStorageAdapter
  /** Optional inactivity boundary. Refresh never records activity. */
  activity?: SessionActivityPolicy
  /**
   * The device's own account list (#1053 4b). A host that does not pass one has
   * no switcher and stores no roster — the affordance a host should not offer
   * is a prop it does not pass, one tier below the UI.
   */
  accounts?: AccountRoster
  /** Injectable for deterministic expiry tests. */
  now?: () => number
}

/**
 * The session owner. One instance per app; `getAccessToken()` is what the SDK
 * transport calls on every request.
 */
export class AuthClient {
  private session: Session | null = null
  private listeners = new Set<(s: Session | null) => void>()
  private readonly storage: SessionStorageAdapter
  private readonly activity?: SessionActivityPolicy
  private readonly accounts?: AccountRoster
  private readonly now: () => number
  private readonly hydrating: Promise<void>
  /** One canonical owner's durable writes, in invocation order. A late save
   * must finish BEFORE a subsequent sign-out clear can report completion. */
  private persistence: Promise<void> = Promise.resolve()
  private sessionEpoch = 0
  /**
   * The in-flight refresh, if any. This is the single most important field here.
   * GoTrue rotates refresh tokens (GOTRUE_SECURITY_REFRESH_TOKEN_ROTATION_ENABLED),
   * so two concurrent refreshes send the SAME refresh token twice: the first
   * rotates it, the second presents a now-revoked token, and reuse detection
   * invalidates the whole family — signing the user out mid-session. The console
   * fires many queries at once on page load, so this is the normal case, not an
   * edge case. Every caller awaits the same promise.
   */
  private refreshing: Promise<Session> | null = null

  constructor(private readonly opts: AuthClientOptions) {
    this.storage = opts.storage ?? localSessionStorage()
    this.activity = opts.activity
    this.accounts = opts.accounts
    this.now = opts.now ?? Date.now

    const loaded = this.storage.load()
    if (loaded && typeof (loaded as Promise<Session | null>).then === 'function') {
      this.hydrating = Promise.resolve(loaded).then((session) => { this.acceptExternal(session, false) })
    } else {
      this.acceptExternal(loaded as Session | null, false)
      this.hydrating = Promise.resolve()
    }
    this.storage.subscribe?.((session) => { this.acceptExternal(session, true) })
  }

  get current(): Session | null { return this.session }
  get isAuthenticated(): boolean { return this.session != null }
  /** Normalized identity-service origin, for a host preflight boundary. */
  get endpointOrigin(): string {
    let parsed: URL
    try { parsed = new URL(this.opts.url) } catch { throw new Error('AuthClient URL must be absolute') }
    if (parsed.username || parsed.password || parsed.search || parsed.hash) {
      throw new Error('AuthClient URL must not contain credentials, query, or fragment')
    }
    return parsed.origin
  }

  /** Async adapters (Chrome storage) hydrate before the first request. */
  async ready(): Promise<Session | null> {
    await this.hydrating
    return this.session
  }

  subscribe(fn: (s: Session | null) => void): () => void {
    this.listeners.add(fn)
    return () => { this.listeners.delete(fn) }
  }

  private emit() { for (const fn of this.listeners) fn(this.session) }

  private acceptExternal(session: Session | null, notify: boolean) {
    this.sessionEpoch += 1
    this.session = validSession(session)
    if (notify) this.emit()
  }

  private async set(s: Session | null) {
    const epoch = ++this.sessionEpoch
    this.session = s
    this.emit()
    const persisted = this.persistence.then(() => s ? this.storage.save(s) : this.storage.clear())
    // A failed save is returned to its caller, but cannot wedge a later clear.
    this.persistence = persisted.catch(() => {})
    await persisted
    if (s && epoch === this.sessionEpoch) {
      /* The ONE place a session becomes current, which is why the device roster
         is recorded here and not in `signIn`. Password sign-in, an accepted
         sign-up grant, an MFA verification and a background refresh all pass
         through this line; recording it in each of them is four chances to
         forget the fifth. `remember` is idempotent per login id. */
      this.accounts?.remember({ userId: s.user.id, email: s.user.email }, this.now())
      /* Whoever they signed in as, this tab is no longer on its way anywhere —
         including when they edited the pre-filled address and signed in as a
         third account instead. Tab-local, so a refresh in a tab that IS signed
         in cannot clear a switch another tab is in the middle of. */
      this.accounts?.setPendingSwitch(null)
    }
  }

  /**
   * End the session on this device WITHOUT forgetting the device's accounts.
   *
   * Extracted so the one line of difference between signing out and switching
   * is visible rather than duplicated: sign-out additionally calls
   * `accounts.forgetAll()`, and a switch deliberately does not. Local-first and
   * unconditional for the same reason `signOut` always was — a sign-out that
   * silently did nothing because the server was unreachable is the worst
   * possible outcome of pressing the button.
   *
   * The outgoing token IS revoked. Leaving a switched-away-from session alive at
   * GoTrue while the device no longer holds it is a dangling token family for an
   * account nobody is using; and since nothing is parked, there is nothing that
   * revocation could have resumed.
   */
  private async leaveSessionKeepingRoster(): Promise<void> {
    const token = this.session?.accessToken
    await this.set(null)
    await this.activity?.clear()
    if (token) {
      try { await this.post('/logout', {}, token) } catch { /* revoked locally regardless */ }
    }
  }

  /**
   * Switch to another account that has signed in on this device (#1053 4b).
   *
   * It **offers, it never grants.** No session is adopted here and none could
   * be: nothing is parked (see `accounts.ts`), so leaving this session means the
   * next one is authenticated afresh through the front door the deployment's MFA
   * and session policy actually cover. The tab remembers the address it is on
   * its way to, so the sign-in form can pre-fill it — in `sessionStorage`,
   * because that intent belongs to one browsing context.
   *
   * `p_user_id`-shaped and yet not a rule-11 problem: the id is looked up in
   * THIS DEVICE's own roster and is used for nothing but an address to type into
   * a form. An id that is not on this device is refused rather than passed on,
   * so a caller cannot use this to learn whether an account exists.
   */
  async switchAccount(userId: string): Promise<{ email: string | null }> {
    await this.ready()
    const target = this.accounts?.find(userId) ?? null
    if (!target) throw new AuthError('That account has not signed in on this device.')
    this.accounts?.setPendingSwitch(target.email)
    await this.leaveSessionKeepingRoster()
    return { email: target.email }
  }

  /** Sign in as somebody this device has never seen. Same act, no target. */
  async addAccount(): Promise<void> {
    await this.ready()
    this.accounts?.setPendingSwitch(null)
    await this.leaveSessionKeepingRoster()
  }

  /** A genuine panel/user interaction. Token refresh deliberately never calls this. */
  async recordActivity(): Promise<void> {
    if (this.activity) await this.activity.record(this.now())
  }

  private async assertActive(): Promise<void> {
    if (!this.session || !this.activity) return
    if (await this.activity.isExpired(this.now())) {
      await this.set(null)
      await this.activity.clear()
      throw new AuthError('Your session has expired after 30 minutes of inactivity. Please sign in again.')
    }
  }

  private async post(path: string, body: unknown, token?: string): Promise<Response> {
    return fetch(`${this.opts.url.replace(/\/$/, '')}${path}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(this.opts.anonKey ? { apikey: this.opts.anonKey } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })
  }

  private async authenticated(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<Response> {
    const token = await this.getAccessToken()
    return fetch(`${this.opts.url.replace(/\/$/, '')}${path}`, {
      method,
      headers: {
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(this.opts.anonKey ? { apikey: this.opts.anonKey } : {}),
        authorization: `Bearer ${token}`,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
  }

  /**
   * List only this caller's safe factor metadata.  The pinned GoTrue version
   * exposes it on GET /user; it has no caller GET /factors route.
   */
  async listMfaFactors(): Promise<MfaFactor[]> {
    const res = await this.authenticated('GET', '/user')
    if (!res.ok) throw new AuthError('Could not load your authentication factors.', res.status)
    const factors = readFactorsFromUser(await res.json())
    if (!factors) throw new AuthError('The authentication service returned an invalid factor list.')
    return factors
  }

  /**
   * Begin TOTP enrolment.  The returned secret envelope is deliberately not a
   * Session and does not serialize.  The UI must call clear() when its setup
   * frame closes, whether verification succeeded or the person cancelled.
   */
  async enrollTotp(friendlyName: string): Promise<TotpEnrollment> {
    const name = friendlyName.trim()
    if (!name || name.length > 100) throw new AuthError('Name this authenticator with 1 to 100 characters.')
    const res = await this.authenticated('POST', '/factors', {
      factor_type: 'totp', friendly_name: name,
    })
    if (!res.ok) throw new AuthError('Could not start authenticator setup.', res.status)
    const enrollment = readTotpEnrollment(await res.json())
    if (!enrollment) throw new AuthError('The authentication service returned an invalid setup response.')
    return enrollment
  }

  async challengeTotp(id: string): Promise<{ id: string; expiresAt: number | null }> {
    const res = await this.authenticated('POST', `/factors/${factorId(id)}/challenge`, {})
    if (!res.ok) throw new AuthError('Could not start the authenticator challenge.', res.status)
    const challenge = await res.json() as Partial<ChallengeResponse>
    if (!challenge.id || !FACTOR_ID.test(challenge.id)) {
      throw new AuthError('The authentication service returned an invalid challenge.')
    }
    return { id: challenge.id, expiresAt: typeof challenge.expires_at === 'number' ? challenge.expires_at : null }
  }

  /** Verify one challenge and atomically adopt the fresh AAL2 session. */
  async verifyTotp(id: string, challengeId: string, value: string): Promise<Session> {
    const res = await this.authenticated('POST', `/factors/${factorId(id)}/verify`, {
      challenge_id: factorId(challengeId), code: totpCode(value),
    })
    if (!res.ok) throw new AuthError('That authenticator code was not accepted.', res.status)
    const token = await res.json() as Partial<TokenResponse>
    if (!token.access_token || !token.refresh_token || typeof token.expires_in !== 'number') {
      throw new AuthError('The authentication service returned an invalid verified session.')
    }
    const session = toSession(token as TokenResponse, this.now())
    await this.set(session)
    await this.recordActivity()
    return session
  }

  async challengeAndVerifyTotp(id: string, value: string): Promise<Session> {
    const challenge = await this.challengeTotp(id)
    return this.verifyTotp(id, challenge.id, value)
  }

  /**
   * Remove one caller-owned factor, then force a token refresh.  A successful
   * deletion followed by a failed refresh signs out rather than leaving a UI
   * that still claims the old AAL2 session.
   */
  async unenrollMfaFactor(id: string): Promise<Session> {
    const res = await this.authenticated('DELETE', `/factors/${factorId(id)}`)
    if (!res.ok) throw new AuthError('Could not remove that authentication factor.', res.status)
    try {
      return await this.refresh()
    } catch {
      await this.set(null)
      throw new AuthError('The factor was removed. Sign in again to continue.')
    }
  }

  // ── OAuth 2.1 consent and grants (#2443 slice 4) ─────────────────────────
  // GoTrue is the authorization server: these read and decide ITS transaction
  // with the person's ordinary session, and store nothing. Shapes and rules:
  // ./oauth.ts. None of these ever returns or logs a code, token or verifier.

  /** Caller-owned recovery-code factor status. A missing set is an honest null. */
  async recoveryCodeStatus(): Promise<RecoveryCodeStatus | null> {
    const res = await this.authenticated('GET', '/factors/recovery-codes')
    if (res.status === 404) {
      const error = await recoveryFailure(res, 'Could not load recovery-code status.', 'status')
      if (error.code === 'mfa_factor_not_found') return null
      throw error
    }
    if (!res.ok) throw await recoveryFailure(res, 'Could not load recovery-code status.', 'status')
    const status = readRecoveryCodeStatus(await res.json().catch(() => null))
    if (!status) throw new AuthError('The authentication service returned invalid recovery-code status.')
    return status
  }

  /** Generate plaintext exactly once; the reveal envelope never serializes it. */
  async generateRecoveryCodes(): Promise<RecoveryCodeReveal> {
    const res = await this.authenticated('POST', '/factors/recovery-codes')
    if (!res.ok) throw await recoveryFailure(res, 'Could not create recovery codes.', 'generate')
    const reveal = readRecoveryCodeReveal(await res.json().catch(() => null))
    if (!reveal) throw new AuthError('The authentication service returned an invalid recovery-code set.')
    return reveal
  }

  /** Replace the whole set. GoTrue atomically invalidates every previous code. */
  async regenerateRecoveryCodes(): Promise<RecoveryCodeReveal> {
    const res = await this.authenticated('POST', '/factors/recovery-codes/regenerate')
    if (!res.ok) throw await recoveryFailure(res, 'Could not replace recovery codes.', 'regenerate')
    const reveal = readRecoveryCodeReveal(await res.json().catch(() => null))
    if (!reveal) throw new AuthError('The authentication service returned an invalid recovery-code set.')
    return reveal
  }

  /** Redeem one code and atomically adopt only a same-person AAL2 session. */
  async verifyRecoveryCode(value: string): Promise<Session> {
    const code = normalizeRecoveryCode(value)
    if (!code) throw new AuthError('Enter a valid recovery code.')
    const before = await this.ready()
    if (!before) throw new AuthError('Sign in before using a recovery code.')
    const res = await this.authenticated('POST', '/factors/recovery-codes/verify', { code })
    if (!res.ok) throw await recoveryFailure(res, 'That recovery code was not accepted.', 'verify')
    const token = await res.json().catch(() => null) as Partial<TokenResponse> | null
    if (!token?.access_token || !token.refresh_token || typeof token.expires_in !== 'number' ||
        !Number.isFinite(token.expires_in) || token.expires_in <= 0) {
      throw new AuthError('The authentication service returned an invalid verified session.')
    }
    const session = toSession(token as TokenResponse, this.now())
    if (!session.user.id || session.user.id !== before.user.id || assuranceOf(session.accessToken) !== 'aal2') {
      throw new AuthError('The authentication service returned an invalid verified session.')
    }
    await this.set(session)
    await this.recordActivity()
    return session
  }

  /** Delete the set, then refresh so the browser cannot retain stale assurance. */
  async deleteRecoveryCodes(): Promise<Session> {
    const res = await this.authenticated('DELETE', '/factors/recovery-codes')
    if (!res.ok) throw await recoveryFailure(res, 'Could not remove recovery codes.', 'delete')
    try {
      return await this.refresh()
    } catch {
      await this.set(null)
      throw new AuthError('Recovery codes were removed. Sign in again to continue.')
    }
  }

  /** Read GoTrue's authorization transaction — bound to the id asked for. */
  async getOAuthAuthorization(id: string): Promise<OAuthAuthorization> {
    const aid = oauthAuthorizationId(id)
    const res = await this.authenticated('GET', `/oauth/authorizations/${aid}`)
    if (!res.ok) throw new AuthError('This authorization request has expired or was already used.', res.status)
    const parsed = readOAuthAuthorization(await res.json().catch(() => null), aid)
    if (!parsed) throw new AuthError('The authentication service returned an invalid authorization request.')
    return parsed
  }

  /** Approve or deny GoTrue's transaction; returns where GoTrue says to go next. */
  async decideOAuthAuthorization(id: string, action: 'approve' | 'deny'): Promise<string> {
    const aid = oauthAuthorizationId(id)
    const res = await this.authenticated('POST', `/oauth/authorizations/${aid}/consent`, { action })
    if (!res.ok) throw new AuthError('This authorization request has expired or was already used.', res.status)
    const url = readOAuthDecision(await res.json().catch(() => null))
    if (!url) throw new AuthError('The authentication service returned no valid redirect.')
    return url
  }

  /** The signed-in person's own grants — GoTrue answers for this session only. */
  async listOAuthGrants(): Promise<OAuthGrant[]> {
    const res = await this.authenticated('GET', '/user/oauth/grants')
    if (!res.ok) throw new AuthError('Could not load your connected apps.', res.status)
    const grants = readOAuthGrants(await res.json().catch(() => null))
    if (!grants) throw new AuthError('The authentication service returned an invalid list of connected apps.')
    return grants
  }

  /** Revoke a grant. GoTrue deletes that client's sessions for this person in
   *  the same transaction, so the NEXT request carrying an issued token fails
   *  core.current_oauth_binding() — revocation is immediate, not at expiry. */
  async revokeOAuthGrant(clientId: string): Promise<void> {
    const cid = oauthClientId(clientId)
    const res = await this.authenticated('DELETE', `/user/oauth/grants?client_id=${encodeURIComponent(cid)}`)
    if (!res.ok) throw new AuthError('Could not disconnect that app.', res.status)
  }

  async signIn(email: string, password: string): Promise<Session> {
    await this.ready()
    const res = await this.post('/token?grant_type=password', { email, password })
    if (!res.ok) {
      const detail = await res.text()
      // Deliberately not distinguishing "no such user" from "wrong password":
      // that difference is a user-enumeration oracle.
      throw new AuthError(
        res.status === 400 || res.status === 401
          ? 'That email and password do not match an account.'
          : `Sign-in failed (${res.status}). ${detail.slice(0, 200)}`,
        res.status,
      )
    }
    const s = toSession((await res.json()) as TokenResponse, this.now())
    await this.set(s)
    await this.recordActivity()
    return s
  }

  /** Explicit Continue only: verify a scanner-safe link through GoTrue, then
   * adopt through the same session owner as every ordinary sign-in. A link
   * supplies no extra assurance; any MFA/step-up remains the existing gate. */
  async exchangeSignInChallenge(challenge: SignInChallenge): Promise<Session> {
    const refused = 'That sign-in link is no longer valid. Request a new one.'
    await this.ready()
    if (!isSignInChallenge(challenge)) throw new AuthError(refused)
    const { tokenHash, verificationType } = challenge
    let retired = false
    let ownAdoption: Session | null = null
    const unsubscribe = this.subscribe((session) => {
      if (ownAdoption && session === ownAdoption) {
        ownAdoption = null // ignore exactly the one synchronous emission we own
      } else {
        retired = true
      }
    })
    try {
      const res = await this.post('/verify', { token_hash: tokenHash, type: verificationType })
      if (!res.ok || retired) throw new AuthError(refused)
      const value: unknown = await res.json()
      if (!value || typeof value !== 'object' || Array.isArray(value)) throw new AuthError(refused)
      const reply = value as Partial<TokenResponse>
      if (typeof reply.access_token !== 'string' || !reply.access_token.trim()
        || typeof reply.refresh_token !== 'string' || !reply.refresh_token.trim()
        || typeof reply.expires_in !== 'number' || !Number.isFinite(reply.expires_in) || reply.expires_in <= 0
        || !reply.user || typeof reply.user.id !== 'string' || !reply.user.id.trim()
        || (reply.user.email != null && typeof reply.user.email !== 'string')) throw new AuthError(refused)
      const session = toSession(reply as TokenResponse, this.now())
      if (retired || !Number.isFinite(session.expiresAt)) throw new AuthError(refused)
      // Ignore only our own synchronous set() emission, never subsequent
      // external changes while its asynchronous persistence completes.
      ownAdoption = session
      const adoption = this.set(session)
      ownAdoption = null
      await adoption
      if (retired) throw new AuthError(refused)
      await this.recordActivity()
      if (retired) throw new AuthError(refused)
      return session
    } catch {
      // No provider diagnostic, challenge or response body reaches the host.
      throw new AuthError(refused)
    } finally {
      unsubscribe()
    }
  }

  /**
   * Ask GoTrue to create an identity. Solum never mints or verifies this token:
   * GoTrue owns authentication, while the host decides whether to expose this
   * opt-in method at all.
   *
   * A duplicate address and an accepted new address deliberately produce the
   * same observable result. GoTrue normally preserves that property itself;
   * the 400/422 fallback keeps it true across supported GoTrue versions.
   */
  async signUp(email: string, password: string, redirectTo?: string): Promise<SignUpResult> {
    await this.ready()
    const qs = redirectTo ? `?redirect_to=${encodeURIComponent(redirectTo)}` : ''
    let res: Response
    try {
      res = await this.post(`/signup${qs}`, { email, password })
    } catch {
      throw new AuthError('Could not create your account. Check your connection and try again.')
    }

    if (!res.ok) {
      // Do not turn GoTrue's deliberately opaque duplicate-account response
      // into an existence oracle through our wrapper.
      if (res.status === 400 || res.status === 422) {
        return { session: null, confirmationRequired: true }
      }
      throw new AuthError(
        res.status === 429
          ? 'Too many attempts. Wait a moment and try again.'
          : `Could not create your account (${res.status}).`,
        res.status,
      )
    }

    const body = await res.json() as Partial<TokenResponse>
    if (body.access_token && body.refresh_token && typeof body.expires_in === 'number') {
      const session = toSession(body as TokenResponse, this.now())
      await this.set(session)
      await this.recordActivity()
      return { session, confirmationRequired: false }
    }
    return { session: null, confirmationRequired: true }
  }

  /** Store the session returned by a confirmed `type=signup` callback. */
  async acceptSignUpGrant(grant: { accessToken: string; refreshToken: string; expiresIn: number }): Promise<Session> {
    await this.ready()
    const session = toSession({
      access_token: grant.accessToken,
      refresh_token: grant.refreshToken,
      expires_in: grant.expiresIn,
    }, this.now())
    await this.set(session)
    await this.recordActivity()
    return session
  }

  /**
   * Sign out — and forget the device.
   *
   * The local clear is FIRST and unconditional; see
   * `leaveSessionKeepingRoster`, which is that behaviour verbatim. The one line
   * this method adds is the whole difference between signing out and switching:
   * *"signing out clears the list, so a shared machine leaks e-mail addresses at
   * worst"* (#1053 comment 17850).
   */
  async signOut(): Promise<void> {
    await this.ready()
    await this.leaveSessionKeepingRoster()
    this.accounts?.forgetAll()
  }

  /** Force a refresh now. Single-flight: concurrent callers share one request. */
  refresh(): Promise<Session> {
    if (this.refreshing) return this.refreshing

    this.refreshing = (async () => {
      await this.ready()
      await this.assertActive()
      const before = this.session?.refreshToken
      if (!before) throw new AuthError('not signed in')

      const rotate = async (): Promise<Session> => {
        // Another trusted context may have rotated while this one waited for
        // the origin lock. Reload and use its result instead of presenting the
        // spent token a second time (which invalidates the whole family).
        const latest = validSession(await this.storage.load())
        if (latest?.refreshToken && latest.refreshToken !== before) {
          this.acceptExternal(latest, true)
          return latest
        }
        if (!latest) throw new AuthError('not signed in')

        const res = await this.post('/token?grant_type=refresh_token', { refresh_token: latest.refreshToken })
        if (!res.ok) {
          await this.set(null)
          throw new AuthError('Your session has expired. Please sign in again.', res.status)
        }
        const s = toSession((await res.json()) as TokenResponse, this.now())
        await this.set(s)
        return s
      }

      return this.storage.withLock
        ? this.storage.withLock('solum.session.refresh', rotate)
        : rotate()
    })().finally(() => { this.refreshing = null })

    return this.refreshing
  }

  /**
   * Ask GoTrue to send this address a recovery link.
   *
   * ── why this is GoTrue's and not ours ──────────────────────────────────────
   *
   * `docs/identity-sso-scim.md` states the platform principle and
   * `modules/portal/0011` repeats it: *Supabase Auth owns AUTHENTICATION; Solum
   * owns AUTHORIZATION and provisioning identity. We never fork the auth
   * system.* A reset token is authentication machinery — generation, hashing at
   * rest, expiry, single use, and the credential write itself. GoTrue already
   * does all five, has been attacked for years, and rate-limits the endpoint
   * (`GOTRUE_RATE_LIMIT_EMAIL_SENT`). Writing a second one in `portal.*` would
   * be a fork of the auth system, and it would make US responsible for exactly
   * the properties that are hardest to get right.
   *
   * So the token is GoTrue's. What the platform owns is the DECISION to send
   * one at all (`portal.request_password_reset` — is this identity local or
   * federated, and is this requester inside the rate limit), the AUDIT ACT, and
   * the DELIVERY. This method is the last hop of that: it is called by the
   * server-side decision path, or by an app that has already been told the
   * route is `local`.
   *
   * ── enumeration ────────────────────────────────────────────────────────────
   *
   * GoTrue answers 200 for an address it has never seen — that is deliberate on
   * its side and we preserve it: this resolves on any non-5xx, so a caller
   * cannot distinguish. It does NOT throw on 400 either, because GoTrue answers
   * 400 for a malformed address, and "we could not parse that" is a weaker but
   * real oracle when the address is well-formed.
   */
  /**
   * Where to send the browser to sign in through the deployment's identity
   * provider — GoTrue's own authorize endpoint (#1156 A5).
   *
   * A URL rather than a method that navigates, because this package is
   * deliberately framework-free and must not decide what "navigate" means for
   * the console, the portal and the extension panel. The caller sets
   * `location.href`.
   *
   * **`sso` and not a named provider, and that is the ruling rather than a
   * default.** Ruled 2026-08-19 (#1156): the portal learns THAT the deployment
   * federates and never with whom — `portal.registration_options().federated`
   * is one boolean, and `access.idp_connection` (which holds the provider's
   * name, protocol and client id) is platform-admin-only by design. So there
   * is no provider name in the browser to put in this URL, and adding a
   * `provider` parameter here would invite a caller to invent one.
   *
   * GoTrue owns the handshake end to end: this is its `/authorize`, not a
   * custom OAuth client in the SPA. Nothing here holds a client id or a secret,
   * which is why the SSO entry point can be a plain link.
   *
   * `redirect_to` comes back to the app's own origin after the round trip.
   * GoTrue matches it against its configured allowlist and refuses anything
   * else, so a tampered value fails at GoTrue rather than sending a token
   * somewhere it should not go.
   */
  ssoAuthorizeUrl(redirectTo?: string): string {
    const base = `${this.opts.url.replace(/\/$/, '')}/sso`
    return redirectTo ? `${base}?redirect_to=${encodeURIComponent(redirectTo)}` : base
  }

  async requestPasswordRecovery(email: string, redirectTo?: string): Promise<void> {
    const qs = redirectTo ? `?redirect_to=${encodeURIComponent(redirectTo)}` : ''
    try {
      await this.post(`/recover${qs}`, { email })
    } catch {
      // A network failure must not become "no such account" in the UI either.
      // The screen says the same thing in every case; see RequestReset.tsx.
    }
  }

  /**
   * Exchange the opaque hash carried by scanner-safe recovery mail.
   *
   * This deliberately does not call `set`: a recovery response is a bearer
   * used for one password write, not a signed-in session. The exchange happens
   * only after the recovery screen's explicit Continue action, so a mail
   * scanner's GET cannot consume the one-time credential before the person.
   */
  async exchangeRecoveryTokenHash(tokenHash: string): Promise<RecoveryGrant> {
    if (!/^[A-Za-z0-9_-]{40,128}$/.test(tokenHash)) {
      throw new AuthError('That reset link is not valid.')
    }
    const res = await this.post('/verify', { token_hash: tokenHash, type: 'recovery' })
    if (!res.ok) {
      throw new AuthError('That reset link is no longer valid. Request a new one.', res.status)
    }
    const session = toSession((await res.json()) as TokenResponse, this.now())
    return {
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      email: session.user.email,
    }
  }

  /**
   * Exchange a scanner-safe INVITE challenge (#2689) — the recovery ceremony,
   * not a sign-in. GoTrue's invite verification gives a password-less invitee
   * a random password and expects the application to make them choose one, so
   * the reply is a one-operation grant exactly like recovery's: held in memory,
   * never `set`, spent by `setPasswordWithRecoveryGrant`, after which the person
   * signs in through the front door their deployment's MFA policy covers.
   * Called only after the landing's explicit Continue.
   */
  async exchangeInviteTokenHash(tokenHash: string): Promise<RecoveryGrant> {
    const refused = 'That invitation link has expired or has already been used. Ask for a new invitation.'
    if (!/^[A-Za-z0-9_-]{40,128}$/.test(tokenHash)) throw new AuthError(refused)
    let res: Response
    try {
      res = await this.post('/verify', { token_hash: tokenHash, type: 'invite' })
    } catch {
      throw new AuthError(refused)
    }
    if (!res.ok) throw new AuthError(refused, res.status)
    const value: unknown = await res.json().catch(() => null)
    const reply = value as Partial<TokenResponse> | null
    if (!reply || typeof reply.access_token !== 'string' || !reply.access_token.trim()) throw new AuthError(refused)
    return {
      accessToken: reply.access_token,
      refreshToken: typeof reply.refresh_token === 'string' ? reply.refresh_token : null,
      email: typeof reply.user?.email === 'string' ? reply.user.email : null,
    }
  }

  /**
   * Confirm a scanner-safe EMAIL-CHANGE challenge (#2689), after the landing's
   * explicit Continue. Confirming an address is not a login: GoTrue answers a
   * completed change with a fresh session, which is revoked at once and never
   * reaches storage (the device keeps whatever session it already had). With
   * secure email change on, the first of the two links answers only a message;
   * that is `awaiting_other_address`, not a failure. No provider text is echoed.
   */
  async confirmEmailChangeTokenHash(tokenHash: string): Promise<'confirmed' | 'awaiting_other_address'> {
    const refused = 'That confirmation link has expired or has already been used. Request the change again.'
    if (!/^[A-Za-z0-9_-]{40,128}$/.test(tokenHash)) throw new AuthError(refused)
    let res: Response
    try {
      res = await this.post('/verify', { token_hash: tokenHash, type: 'email_change' })
    } catch {
      throw new AuthError(refused)
    }
    if (!res.ok) throw new AuthError(refused, res.status)
    const value: unknown = await res.json().catch(() => null)
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new AuthError(refused)
    const reply = value as Partial<TokenResponse> & { msg?: unknown }
    if (typeof reply.access_token === 'string' && reply.access_token.trim()) {
      // Retire the session GoTrue minted for this click. Its failure is
      // swallowed: the address IS changed, and the minted session was never
      // stored, so nothing on this device can use it.
      try { await this.post('/logout?scope=local', {}, reply.access_token) } catch { /* see above */ }
      return 'confirmed'
    }
    if (typeof reply.msg === 'string' && reply.access_token === undefined) return 'awaiting_other_address'
    throw new AuthError(refused)
  }

  /**
   * Set a new password using a recovery grant, then destroy every session.
   *
   * ── the three properties this method exists to hold ────────────────────────
   *
   * 1. **A completed reset is not a login.** GoTrue's recovery redirect hands
   *    the browser a real access token — so the naive flow leaves the person
   *    signed in, which means clicking a link in an email is enough to be
   *    inside the portal. Anyone with momentary access to the mailbox (a shared
   *    inbox, a forwarded message, a mail client left open) is then a user. So
   *    the grant is spent on ONE operation and revoked; the person signs in
   *    afterwards, with the password they just chose, through the front door
   *    that the deployment's MFA and session policy actually cover.
   *
   * 2. **Existing sessions die.** The whole reason a person resets a password
   *    is often that somebody else has it. If the thief's session survives, the
   *    reset accomplished nothing — it merely locked the owner into sharing.
   *    `scope=global` revokes every refresh token for the user, on every
   *    device, which is the only revocation that means anything given tokens
   *    are held in localStorage (see the STORAGE note at the top of this file).
   *
   * 3. **The local device is cleared unconditionally.** Same reasoning as
   *    `signOut`: if the revoke call fails, the person must still not be left
   *    holding a session on this machine.
   *
   * The order is deliberate — password first, revoke second. Revoking first
   * would invalidate the grant we need for the write, and a failure between the
   * two leaves the password changed and the sessions alive, which is recoverable
   * (sign in, sign out everywhere) where the reverse is not.
   *
   * @param grantToken the access token from the recovery fragment (never stored)
   */
  async setPasswordWithRecoveryGrant(
    grantToken: string, newPassword: string, link: 'reset' | 'invitation' = 'reset',
  ): Promise<void> {
    const res = await fetch(`${this.opts.url.replace(/\/$/, '')}/user`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        ...(this.opts.anonKey ? { apikey: this.opts.anonKey } : {}),
        authorization: `Bearer ${grantToken}`,
      },
      body: JSON.stringify({ password: newPassword }),
    })

    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      // 401/403 here means the grant is spent, expired, or was never valid.
      // Named separately from a policy rejection because the person's next
      // action differs: request a new link, versus pick a different password.
      throw new AuthError(
        res.status === 401 || res.status === 403
          ? `That ${link} link is no longer valid. ${link === 'reset' ? 'Request a new one.' : 'Ask for a new invitation.'}`
          : res.status === 422 || res.status === 400
            ? `That password was refused. ${detail.slice(0, 200)}`
            : `Could not set your password (${res.status}).`,
        res.status,
      )
    }

    // Property 1 and 2, in one call. Failure is swallowed on purpose: the
    // password IS changed at this point, and throwing here would show the
    // person an error for an operation that succeeded, inviting them to do it
    // again with a link that is now spent.
    try {
      await fetch(`${this.opts.url.replace(/\/$/, '')}/logout?scope=global`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(this.opts.anonKey ? { apikey: this.opts.anonKey } : {}),
          authorization: `Bearer ${grantToken}`,
        },
        body: '{}',
      })
    } catch {
      /* the local clear below still happens; see property 3 */
    }

    // Property 3. Also covers the case where this tab held an OLD session for
    // the same or a different user before the link was opened.
    await this.set(null)
    await this.activity?.clear()
  }

  /**
   * The current access token, refreshed if it is at or near expiry. This is the
   * function handed to the SDK transport — so every request self-heals rather
   * than failing once and forcing a reload.
   */
  async getAccessToken(): Promise<string> {
    await this.ready()
    await this.assertActive()
    const s = this.session
    if (!s) throw new AuthError('not signed in')
    if (this.now() < s.expiresAt - REFRESH_SKEW_MS) return s.accessToken
    return (await this.refresh()).accessToken
  }
}
